import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Collapse,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DeleteModal from "../DeleteModal/DeleteModal";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import "./GuestCard.css";

const API = process.env.REACT_APP_API_BASE;

const translateRelationship = (rel) => {
  const map = {
    FAMILY: "Семејство",
    FRIEND: "Пријател",
    COLLEAGUE: "Колега",
    OTHER: "Друго",
  };
  return map[rel] || rel;
};

const translateAge = (age) => {
  const map = {
    ADULT: "Возрасен",
    CHILD: "Дете",
    BABY: "Бебе",
  };
  return map[age] || age;
};

const GuestCard = ({
  mainGuest,
  sideGuests = [],
  tables,
  setTables,
  onEdit,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const groupSize = 1 + sideGuests.length;
  const expandable = sideGuests.length > 0;
  const availableTables = tables.filter((t) => t.available_seats >= groupSize);

  const handleExpandClick = () => setExpanded(!expanded);

  const handleDeleteConfirm = async () => {
    try {
      await fetchWithAuth(
        `${API}/remove-seats-group?group_id=${mainGuest.group_id}`,
        { method: "POST" }
      );

      const allGuests = [mainGuest, ...sideGuests];
      await Promise.all(
        allGuests.map((guest) =>
          fetchWithAuth(`${API}/guests/${guest.id}`, { method: "DELETE" })
        )
      );

      setDeleteOpen(false);
      if (onDelete) onDelete(mainGuest.id);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSeatSelect = async (table) => {
    try {
      if (mainGuest.table_number) {
        await fetchWithAuth(`${API}/remove-seats-group?group_id=${mainGuest.group_id}`, { method: "POST" });
      }
  
      await fetchWithAuth(`${API}/create-seats-group`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          table_id: table.id,
          group_id: mainGuest.group_id || mainGuest.id,
        }),
      });
  
      mainGuest.table_number = table.number;
      mainGuest.seated = true;
      setAnchorEl(null);
  
      // Refresh tables globally
      const response = await fetchWithAuth(`${API}/get-tables`, { method: "POST" });
      if (!response.ok) throw new Error("Failed to fetch tables");
      const data = await response.json();
      setTables(data); // update global tables
    } catch (err) {
      console.error(err);
    }
  };
  

  return (
    <>
      <Card className="guest-card">
        <CardContent className="guest-card-content">
          <Box className="guest-main-row">
            <Box className="guest-card-info">
              <Typography className="guest-name">
                {mainGuest.name} {mainGuest.last_name}
              </Typography>
              <Typography className="guest-relation">
                {translateRelationship(mainGuest.relationship_category)} |{" "}
                {translateAge(mainGuest.age_category)}
              </Typography>

              <Box mt={1} display="flex" alignItems="center" gap={1}>
                <Chip
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {mainGuest.table_number
                        ? `Маса бр. ${mainGuest.table_number}`
                        : "Нема маса"}
                      {!mainGuest.seated && tables.length > 0 && (
                        <ArrowDropDownIcon fontSize="small" />
                      )}
                    </Box>
                  }
                  size="small"
                  className={`table-chip ${
                    mainGuest.seated ? "seated" : "not-seated"
                  } clickable`}
                  onClick={(e) =>
                    tables.length > 0 && setAnchorEl(e.currentTarget)
                  }
                />
              </Box>
            </Box>

            <Box className="guest-group-meta">
              <EditIcon
                className="icon clickable"
                onClick={() => onEdit({ mainGuest, sideGuests })}
              />
              <DeleteIcon
                className="icon clickable"
                onClick={() => {
                  setDeleteOpen(true);
                }}
              />
              <PersonIcon className="icon" />
              <Typography variant="body2" className="meta-number">
                {groupSize}
              </Typography>
              {expandable && (
                <ExpandMoreIcon
                  className={`icon expand-icon ${expanded ? "expanded" : ""}`}
                  onClick={handleExpandClick}
                />
              )}
            </Box>
          </Box>

          {expandable && (
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box className="guest-expanded-info">
                <Typography
                  variant="subtitle2"
                  className="expanded-section-title"
                >
                  Придружници
                </Typography>
                {sideGuests.map((guest) => (
                  <Box key={guest.id} className="card-info">
                    <Box className="guest-info-text">
                      <Typography className="guest-name">
                        {guest.name} {guest.last_name}
                      </Typography>
                      <Typography className="guest-relation">
                        {translateRelationship(guest.relationship_category)} |{" "}
                        {translateAge(guest.age_category)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Collapse>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {availableTables.filter((t) => t.number !== mainGuest.table_number)
          .length > 0 ? (
          availableTables
            .filter((t) => t.number !== mainGuest.table_number)
            .map((table) => (
              <MenuItem key={table.id} onClick={() => handleSeatSelect(table)}>
                {`Маса ${table.number} (${table.available_seats} слободни столчиња)`}
              </MenuItem>
            ))
        ) : (
          <MenuItem disabled>Нема достапни маси за {groupSize} луѓе</MenuItem>
        )}
      </Menu>

      <DeleteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        secondaryText="Бришењето на главниот гостин ќе ги избрише и останатите гости придружници гости."
      />
    </>
  );
};

export default GuestCard;
